/**
 * App Routes Configuration
 * Composes routes from all feature modules
 */

import { createBrowserRouter, Navigate } from "react-router-dom";
import { authRoutes } from "@/features/auth/routes";
import { catalogRoutes } from "@/features/catalog/routes";
import { contractorProfileRoutes } from "@/features/contractor-profile/routes";
import { dashboardRoutes } from "@/features/dashboard/routes";
import { marketplaceRoutes } from "@/features/marketplace/routes";
import { onboardingRoutes } from "@/features/onboarding/routes";
import { portfolioRoutes } from "@/features/portfolio/routes";
import { projectsRoutes } from "@/features/projects/routes";
import { realtorRoutes } from "@/features/realtor/routes";
import { settingsRoutes } from "@/features/settings/routes";
import { sharingRoutes } from "@/features/sharing/routes";
import { THEME_PROPOSAL_IDS } from "@/features/styleguide/proposals/ids";

export const router = createBrowserRouter([
	...authRoutes,
	...onboardingRoutes,
	...dashboardRoutes,
	...projectsRoutes,
	...marketplaceRoutes,
	...catalogRoutes,
	...settingsRoutes,
	...portfolioRoutes,
	...contractorProfileRoutes,
	...sharingRoutes,
	...realtorRoutes,
	// The playground and styleguide are review-only tools, gated to dev builds.
	// They load lazily: in a production build this branch is dead code, so
	// their modules are not bundled.
	...(import.meta.env.DEV
		? [
				{
					path: "/studio/playground",
					lazy: async () => ({
						Component: (
							await import("@/features/studio/pages/studio-playground")
						).StudioPlayground,
					}),
				},
				{
					path: "/styleguide",
					lazy: async () => ({
						Component: (
							await import("@/features/styleguide/pages/theme-styleguide")
						).ThemeStyleguide,
					}),
				},
				...THEME_PROPOSAL_IDS.map((id) => ({
					path: `/styleguide-${id}`,
					lazy: async () => {
						const [{ ThemeProposalPage }, { THEME_PROPOSALS }] =
							await Promise.all([
								import("@/features/styleguide/pages/theme-proposal"),
								import("@/features/styleguide/proposals/themes"),
							]);
						const theme = THEME_PROPOSALS.find((t) => t.id === id);
						if (!theme) throw new Error(`No theme proposal ${id}`);
						return { Component: () => <ThemeProposalPage theme={theme} /> };
					},
				})),
			]
		: []),

	// Root redirect
	{
		path: "/",
		element: <Navigate to="/login" replace />,
	},

	// Legacy routes
	{
		path: "/signup",
		element: <Navigate to="/login" replace />,
	},
	{
		path: "/finishd",
		element: <Navigate to="/welcome" replace />,
	},

	// Catch-all
	{
		path: "*",
		element: <Navigate to="/login" replace />,
	},
]);
